import { createFileRoute, redirect } from '@tanstack/react-router';
import { seoHead } from '@/lib/seoHead';
import { ProductPage, productQuery } from '@/components/product/ProductPage';
import { comparisonQuery } from '@/components/comparison/ComparisonPages';
import { ComparisonError } from '@/components/comparison/ComparisonError';
import { money } from '@/lib/comparison';

const month = () => new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'America/Sao_Paulo' }).format(new Date());
const fit = (base: string, tail: string) => (base.length + tail.length <= 70 ? base + tail : base.slice(0, Math.max(20, 70 - tail.length - 1)).trimEnd() + '…' + tail);

export const Route = createFileRoute('/produto/$slug')({
  loader: async ({ context, params }) => {
    const p = await context.queryClient.ensureQueryData(productQuery(params.slug));
    if (p.kind === 'redirect') throw redirect({ to: '/produto/$slug', params: { slug: p.slug }, statusCode: 301 });
    if (p.kind === 'comparison') {
      const c = await context.queryClient.ensureQueryData(comparisonQuery({ id: p.id }));
      return { p, cmp: c.items[0] ?? null };
    }
    return { p, cmp: null };
  },
  head: ({ loaderData, params }) => {
    const path = `/produto/${params.slug}`;
    const p = loaderData?.p;
    if (p?.kind === 'comparison' && loaderData?.cmp) {
      const c = loaderData.cmp; const low = Math.min(...c.offers.map(o => o.price));
      return seoHead({ title: fit(c.title, ` | Menor preço em ${c.offers.length} lojas`),
        description: `${c.title} a partir de ${money(low)}. Compare o preço em ${c.offers.length} lojas parceiras, veja o histórico e compre gerando doação para uma causa social.`,
        path, ogType: 'product', ogImage: c.image_url?.startsWith('https://') ? c.image_url : null });
    }
    if (p?.kind === 'deal') {
      const d = p.deal;
      const title = p.stale || !(d.discount_percent > 0) ? fit(d.title, p.stale ? ` (esgotado) | ${d.brand.name}` : ` | ${d.brand.name}`) : fit(d.title, ` com ${d.discount_percent}% OFF na ${d.brand.name}`);
      return seoHead({ title,
        description: p.stale ? `${d.title} na ${d.brand.name}: produto esgotado no momento. Veja o histórico de preço, ative o alerta de volta e confira similares.` : `${d.title} por ${money(d.price)}${d.discount_percent > 0 ? ` (de ${money(d.original_price)})` : ''} na ${d.brand.name}, ${month()}. Compre com desconto real e gere doação para uma causa social.`,
        path, ogType: 'product', ogImage: d.image_url?.startsWith('https://') ? d.image_url : null });
    }
    return seoHead({ title: 'Produto indisponível | Shopping do Bem', description: 'Este produto não está mais disponível nas lojas parceiras. Veja as ofertas do dia.', noindex: true });
  },
  component: function Produto() { return <ProductPage slug={Route.useParams().slug} />; },
  errorComponent: ({ reset }) => <ComparisonError reset={reset} />,
});
